import { useEffect, useState } from 'react';
import { Loader2, LogOut } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import {
  fetchMyAccount,
  fetchMyProfile,
  listAssessmentAttempts,
  listCheckins,
  listMyAssessments,
  listMyPurchases,
  listMyReviews,
  type AssessmentType,
  type CheckinRow,
  type FreeResult,
  type MyPurchase,
  type MyReview,
} from '../../lib/apiClient';
import { usePageMeta } from '../../lib/usePageMeta';
import { Button } from '../ui/button';
import { AdminPanelEntry } from './AdminPanelEntry';
import { AssessmentHistory } from './AssessmentHistory';
import { AssessmentsTab } from './AssessmentsTab';
import { CheckinTab } from './CheckinTab';
import { SettingsTab } from './SettingsTab';
import { HpSummary } from './HpSummary';
import { NextBestAction } from './NextBestAction';
import { latestOf } from '../../lib/dashboard/overview';
import { ReportsTab } from './ReportsTab';

/**
 * `/dashboard` — the customer's dashboard, migrated from production.
 *
 * **This is a migration surface.** Production serves a tabbed page: a header, a
 * five-tab strip, and one tab's content at a time. The header text, the tab
 * labels, the tab order, and the markup beneath them are copied rather than
 * decided here.
 *
 * All five of production's tabs are registered.
 *
 * V2's own greeting heading is gone: production's header is `My Dashboard`, and
 * a page cannot carry two first-level headings. The header replaced it rather
 * than being added beside it.
 *
 * PARITY TODO: V2's assessment history still stands at the foot of the Overview
 * tab. Production has no such section; it is kept until the remaining Overview
 * pieces land so nothing that works today is lost.
 */
type Tab = 'overview' | 'assessments' | 'reports' | 'checkin' | 'settings';

const TAB_LABELS: Record<Tab, string> = {
  overview: 'Overview',
  assessments: 'Assessments',
  reports: 'Reports',
  checkin: 'Check-in',
  settings: 'Settings',
};

const TABS: readonly Tab[] = ['overview', 'assessments', 'reports', 'checkin', 'settings'];

export function Dashboard() {
  usePageMeta({
    title: 'Your High Performer OS Dashboard',
    description:
      'View your High Performer Type, assessment results, reset plan, workbook access, and reassessment eligibility.',
    noindex: true,
  });

  const { status, session, signOut } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [completed, setCompleted] = useState<ReadonlySet<AssessmentType> | null>(null);
  const [assessments, setAssessments] = useState<FreeResult[]>([]);
  /**
   * Production's Summary heads itself with the customer's name and falls back
   * to a generic title. A missing profile is a normal state for a new account,
   * so it is a fallback rather than an error.
   */
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [checkins, setCheckins] = useState<CheckinRow[]>([]);
  const [supportAccessGrantedAt, setSupportAccessGrantedAt] = useState<string | null>(null);
  const [purchases, setPurchases] = useState<MyPurchase[]>([]);
  const [reviews, setReviews] = useState<MyReview[]>([]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    void (async () => {
      // Production loads the dashboard's data together and shows one spinner
      // until it has it.
      const [res, mine, profile, history, account, bought, written] = await Promise.all([
        listAssessmentAttempts(token),
        listMyAssessments(token),
        fetchMyProfile(token),
        listCheckins(token),
        fetchMyAccount(token),
        listMyPurchases(token),
        listMyReviews(token),
      ]);
      if (cancelled) return;

      setCheckins(history.state === 'ok' ? history.checkins : []);
      setSupportAccessGrantedAt(account.state === 'ok' ? account.supportAccessGrantedAt : null);
      setPurchases(bought.state === 'ok' ? bought.purchases : []);
      setReviews(written.state === 'ok' ? written.reviews : []);

      setDisplayName(
        profile.state === 'ok'
          ? ([profile.profile.first_name, profile.profile.last_name]
              .filter(Boolean)
              .join(' ')
              .trim() || null)
          : null,
      );

      // A failed read is an empty list, not a spinner that never resolves: the
      // Reports tab then reads its own empty state, which is what an account
      // with no assessments sees.
      setAssessments(mine.state === 'ok' ? mine.assessments : []);
      // A failed read is an empty set, not a spinner that never resolves: the
      // cards then read "Not Started", which is what an account with no
      // assessments sees, and every other section on the page still renders.
      setCompleted(
        new Set(
          res.state === 'ok'
            ? res.attempts
                .filter((attempt) => attempt.status === 'completed')
                .map((attempt) => attempt.assessment_type)
            : [],
        ),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (completed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="flex flex-col" data-testid="dashboard">
      {/* ── Header ─────────────────────────────────────────── */}
      <header className="flex flex-wrap justify-between items-center gap-4 pb-6 border-b border-border">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Your assessments, reports, and daily tracking.
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <AdminPanelEntry />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void signOut()}
            data-testid="dashboard-sign-out"
            className="text-muted-foreground hover:text-foreground gap-2"
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            Sign Out
          </Button>
        </div>
      </header>

      {/* ── Tab Navigation ──────────────────────────────────── */}
      <nav
        className="border-b border-border flex gap-6 overflow-x-auto overflow-y-hidden -mx-4 px-4"
        aria-label="Dashboard sections"
        data-testid="dashboard-tabs"
      >
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            aria-current={activeTab === tab ? 'page' : undefined}
            data-testid={`dashboard-tab-${tab}`}
            className={`pb-3 pt-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' ? (
        <div className="space-y-8 pt-6" data-testid="dashboard-panel-overview">
          <NextBestAction assessments={assessments} />
          <HpSummary assessments={assessments} displayName={displayName} />
          <AssessmentHistory />
        </div>
      ) : null}

      {activeTab === 'assessments' ? <AssessmentsTab completed={completed} /> : null}

      {activeTab === 'reports' ? <ReportsTab assessments={assessments} /> : null}

      {activeTab === 'checkin' ? (
        <CheckinTab
          latestBottleneck={latestOf(assessments, 'bottleneck')}
          checkins={checkins}
          // Newest first, which is the order the history renders in.
          onSaved={(saved) => setCheckins((current) => [saved, ...current])}
        />
      ) : null}

      {activeTab === 'settings' ? (
        <SettingsTab
          supportAccessGrantedAt={supportAccessGrantedAt}
          onSupportAccessChanged={setSupportAccessGrantedAt}
          // Production's eligibility: a completed purchase with no review.
          purchasesNeedingReview={purchases.filter(
            (purchase) => !reviews.some((review) => review.purchaseId === purchase.id),
          )}
          onReviewed={(review) => setReviews((current) => [review, ...current])}
        />
      ) : null}
    </div>
  );
}
